import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { agentAxiosObj } from "../../../config/apiClients";
import { AgentDashboardState } from "../../types";

const initialState: AgentDashboardState = {
  stats: null,
  customStats: null,
  isLoading: false,
  birthdayClients: [],
  isBirthdaysLoading: false,
  kycExpiredClients: [],
  isKycExpiredLoading: false,
};

export const getAgentStats = createAsyncThunk(
  "agentDashboard/getStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agentAxiosObj.get(`/dashboard/stats`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.payload?.message || error.message || "Failed to load stats"
      );
    }
  }
);

export const getAgentCustomDateStats = createAsyncThunk(
  "agentDashboard/getCustomDateStats",
  async ({ startDate, endDate }: { startDate: string; endDate: string }, { rejectWithValue }) => {
    try {
      const response = await agentAxiosObj.get(`/dashboard/stats?startDate=${startDate}&endDate=${endDate}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.payload?.message || error.message || "Failed to load stats"
      );
    }
  }
);

export const getAgentClientBirthdays = createAsyncThunk(
  "agentDashboard/getClientBirthdays",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agentAxiosObj.get(`/dashboard/client-birthdays`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.payload?.message ||
          error.message ||
          "Failed to load client birthdays",
      );
    }
  },
);

export const getAgentClientKycExpired = createAsyncThunk(
  "agentDashboard/getClientKycExpired",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agentAxiosObj.get(`/dashboard/client-kyc-expired`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.payload?.message ||
          error.message ||
          "Failed to load clients with expired KYC",
      );
    }
  },
);

const agentDashboardSlice = createSlice({
  name: "agentDashboard",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAgentStats.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAgentStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload?.payload || action.payload;
      })
      .addCase(getAgentStats.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getAgentCustomDateStats.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAgentCustomDateStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customStats = action.payload?.payload.custom_range;
      })
      .addCase(getAgentCustomDateStats.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getAgentClientBirthdays.pending, (state) => {
        state.isBirthdaysLoading = true;
      })
      .addCase(getAgentClientBirthdays.fulfilled, (state, action) => {
        state.isBirthdaysLoading = false;
        state.birthdayClients = action.payload?.payload?.clients ?? [];
      })
      .addCase(getAgentClientBirthdays.rejected, (state) => {
        state.isBirthdaysLoading = false;
        state.birthdayClients = [];
      })
      .addCase(getAgentClientKycExpired.pending, (state) => {
        state.isKycExpiredLoading = true;
      })
      .addCase(getAgentClientKycExpired.fulfilled, (state, action) => {
        state.isKycExpiredLoading = false;
        state.kycExpiredClients = action.payload?.payload?.clients ?? [];
      })
      .addCase(getAgentClientKycExpired.rejected, (state) => {
        state.isKycExpiredLoading = false;
        state.kycExpiredClients = [];
      });
  },
});

export default agentDashboardSlice.reducer;